import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../../Api/config";
import { MdOutlineEventAvailable, MdDelete, MdCheck } from "react-icons/md";

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [bikesMap, setBikesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch bikes and bookings concurrently
      const [bikesRes, bookingsRes] = await Promise.all([
        axiosInstance.get("/motorcycles"),
        axiosInstance.get("/bike-bookings"),
      ]);

      // Create a lookup map for motorcycle ID -> Name
      const map = {};
      (bikesRes.data || []).forEach((bike) => {
        map[bike.id] = bike.name;
      });
      setBikesMap(map);

      // Set bookings
      setBookings(bookingsRes.data || []);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError("Failed to fetch test ride bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const res = await axiosInstance.post(`/bike-bookings/status/${id}`, {
        status: newStatus,
      });
      if (res.data.success) {
        setSuccessMsg(`Booking status updated to ${newStatus}!`);
        fetchData();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to update status.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try {
      const res = await axiosInstance.delete(`/bike-bookings/${id}`);
      if (res.data.success) {
        setSuccessMsg("Booking record deleted.");
        fetchData();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to delete booking.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-600">Admin</p>
        <h1 className="mt-2 text-3xl font-bold text-slate-950">Test Ride Bookings</h1>
      </div>

      {successMsg && (
        <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-3 rounded-lg text-sm font-semibold">
          {successMsg}
        </div>
      )}

      {error && (
        <div className="bg-rose-50 text-rose-605 border border-rose-100 px-4 py-3 rounded-lg text-sm font-medium">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 shadow-sm">
          <MdOutlineEventAvailable className="text-5xl mx-auto text-slate-300 mb-3" />
          <p>No test ride bookings received yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="p-4">Customer Details</th>
                  <th className="p-4">Motorcycle</th>
                  <th className="p-4">Message</th>
                  <th className="p-4">Booking Date</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-150">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-4">
                      <div className="font-semibold text-slate-900">{booking.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{booking.phone}</div>
                      {booking.email && <div className="text-xs text-slate-400 mt-0.5">{booking.email}</div>}
                      {booking.city && <div className="text-[10px] text-slate-450 uppercase font-bold tracking-wider mt-1">{booking.city}</div>}
                    </td>
                    <td className="p-4 font-semibold text-slate-800">
                      {bikesMap[booking.motorcycle_id] || "General Test Ride"}
                    </td>
                    <td className="p-4 text-slate-600 max-w-xs truncate" title={booking.message}>
                      {booking.message || <span className="text-slate-350 italic">No notes</span>}
                    </td>
                    <td className="p-4 text-slate-500 font-medium">
                      {new Date(booking.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="p-4">
                      <select
                        value={booking.status}
                        onChange={(e) => handleUpdateStatus(booking.id, e.target.value)}
                        className={`text-xs font-bold rounded-lg px-2.5 py-1.5 border outline-none cursor-pointer transition ${
                          booking.status === "new"
                            ? "bg-blue-50 text-blue-700 border-blue-200"
                            : booking.status === "contacted"
                            ? "bg-amber-50 text-amber-700 border-amber-250"
                            : booking.status === "scheduled"
                            ? "bg-purple-50 text-purple-750 border-purple-200"
                            : booking.status === "completed"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-250"
                            : "bg-slate-100 text-slate-500 border-slate-300"
                        }`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => handleDelete(booking.id)}
                        className="p-2 text-slate-550 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                        title="Delete"
                      >
                        <MdDelete className="text-lg" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
